import type { RateRowView, RatesApplied } from "@checkone/contract/client";
import { Fragment } from "react";
import { PAYEE_LABEL, formatDateTime, formatDay, pct, sectionLabel } from "../../lib/format";

interface Pair {
  payee: RateRowView["payee_type"];
  on: RateRowView | null;
  not: RateRowView | null;
}

/** Rows of one version as section → payee type → on-ATL and not-on-ATL rate, in the table's order. */
export function rateGroups(rows: readonly RateRowView[]): { section: string; pairs: Pair[] }[] {
  const out: { section: string; pairs: Pair[] }[] = [];
  for (const r of rows) {
    let g = out.find((x) => x.section === r.section);
    if (!g) out.push((g = { section: r.section, pairs: [] }));
    let p = g.pairs.find((x) => x.payee === r.payee_type);
    if (!p) g.pairs.push((p = { payee: r.payee_type, on: null, not: null }));
    p[r.atl === "on_atl" ? "on" : "not"] = r;
  }
  return out;
}

function Rate({ row }: { row: RateRowView | null }) {
  if (!row) return <span className="muted">Not in table</span>;
  return (
    <>
      <span className="mono">{pct(row.rate_pct)}</span>
      {row.note && <div className="subtle">{row.note}</div>}
    </>
  );
}

/**
 * "Rates applied this week": every row of the approved rate-table version the check used, so each rate
 * quoted in the changes table can be read against its source. Collapsed by default, as in the artboard.
 * With no approved version the check quoted no rate, and the page says so instead of showing a table.
 */
export function RatesAppliedTable({ applied }: { applied: RatesApplied | null }) {
  if (!applied)
    return (
      <section className="callout" aria-labelledby="rates-h">
        <p>
          <strong id="rates-h">Rate table pending approval.</strong> No approved rate table was in force for
          this check, so no withholding rate was quoted. Rates appear once a version is signed by two people.
        </p>
      </section>
    );
  const { rate_table: t } = applied;
  return (
    <details className="fold" aria-label="Rates applied this week">
      <summary>
        <span>
          Rates applied this week · table <span className="mono">v{t.version}</span>, effective{" "}
          {formatDay(t.effective_from)}
        </span>
      </summary>
      <div className="fold-body">
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <table className="data compact rates">
            <caption className="sr-only">
              Withholding rates in rate table version {t.version}, effective {formatDay(t.effective_from)}
            </caption>
            <thead>
              <tr>
                <th scope="col">Section</th>
                <th scope="col">Payee</th>
                <th scope="col">On ATL</th>
                <th scope="col">Not on ATL</th>
              </tr>
            </thead>
            <tbody>
              {rateGroups(applied.rows).map((g) => (
                <Fragment key={g.section}>
                  {g.pairs.map((p, i) => (
                    <tr key={`${g.section}-${p.payee}`}>
                      <th scope="row">
                        {i === 0 ? (
                          sectionLabel(g.section)
                        ) : (
                          <span className="sr-only">{sectionLabel(g.section)}</span>
                        )}
                      </th>
                      <td>{PAYEE_LABEL[p.payee]}</td>
                      <td>
                        <Rate row={p.on} />
                      </td>
                      <td>
                        <Rate row={p.not} />
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="subtle" style={{ margin: 0, padding: "var(--space-3) 1.25rem" }}>
          Source: {applied.source_note}. Approved by two people {formatDateTime(applied.approved_at)}. FBR's
          published rates per section; no amounts, certificates or SROs.
        </p>
      </div>
    </details>
  );
}
